import { useMemo } from 'react';

import { RIGHTS_IS_ON, RIGHTS_MODULES } from '@app/constants/accessRights';
import { initialAccessRights } from '@app/data';
import { useMemoizeObject } from '@app/hooks/useMemoizeObject';
import { useNewUserAccessRights } from '@app/hooks/useNewUserAccessRights';
import { selectAccessRights, useAppSelector } from '@app/store';
import type { NAccessRights } from '@app/types';
import { hasModuleRight } from '@app/utils/hasModuleRight';

export const useAccessRights = (
  entityRightsId: keyof NAccessRights.PropsWithSpecificRights,
  specificRightId: unknown
) => {
  const { userAccessRights } = useNewUserAccessRights();
  const accessRights = useAppSelector(selectAccessRights);
  const isAdminRights = accessRights?.IsAdmin;
  const isSuperAdminRights = accessRights?.IsSuperAdmin;

  const memoizedAccessRights =
    useMemoizeObject(accessRights) || initialAccessRights;

  const hasUserRight =
    userAccessRights?.[entityRightsId]?.[
      specificRightId as keyof (typeof userAccessRights)[typeof entityRightsId]
    ];

  const hasUserRightForModuleView_: boolean = useMemo(() => {
    const enabledModules = memoizedAccessRights?.[RIGHTS_MODULES];

    if (!enabledModules) return false;

    return (
      (hasModuleRight(entityRightsId, enabledModules) &&
        (isSuperAdminRights ||
          isAdminRights ||
          memoizedAccessRights?.[entityRightsId]?.[RIGHTS_IS_ON])) ||
      false
    );
  }, [entityRightsId, isSuperAdminRights, isAdminRights, memoizedAccessRights]);

  hasUserRightForModuleView_;

  const hasUserRightForModuleView: boolean = useMemo(() => {
    const enabledModules = userAccessRights?.[RIGHTS_MODULES];

    if (!enabledModules) return false;

    return (
      (hasModuleRight(entityRightsId, enabledModules) &&
        (isSuperAdminRights ||
          isAdminRights ||
          userAccessRights?.[entityRightsId]?.[RIGHTS_IS_ON])) ||
      false
    );
  }, [entityRightsId, isSuperAdminRights, isAdminRights, userAccessRights]);

  return {
    hasUserRight,
    hasUserRightForModuleView,
  };
};
